import React, { useEffect, useMemo, useState } from 'react';

import EventBusyOutlinedIcon from '@mui/icons-material/EventBusyOutlined';
import {
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';

import AvailabilityDocumentUploadZone from '~/components/Vendors/Properties/AvailabilityDocumentUploadZone';

import { useExtractStopsellDates } from '~/hooks/apis/stopsell/useExtractStopsellDates';

import { CatalogRoom, StopsellExtraction } from '~/types/stopsell';

import { matchRoomsToCatalog } from '~/utils/stopsellRoomMatching';
import { pluralizeToString } from '~/utils/stringUtils';

import type { RoomRateSelection } from '../surcharges/surchargeUtils';

import BlackoutApplyReviewTable from './BlackoutApplyReviewTable';
import { SourceBlackoutRange } from './blackoutReconciliation';
import type { SalesforceBlackoutDate } from './types';
import { useBlackoutApplyPlan } from './useBlackoutApplyPlan';

interface Props {
  blackoutDatesByRoomRate: Record<string, Array<App.BlackoutDate>>;
  onClose: () => void;
  propertyId: string;
  roomRateSelections: Array<RoomRateSelection>;
}

const UNMAPPED = '';
const NO_SALESFORCE_BLACKOUT_DATES: Array<SalesforceBlackoutDate> = [];

export default function UploadStopsellDocumentModal({
  blackoutDatesByRoomRate,
  onClose,
  propertyId,
  roomRateSelections,
}: Props) {
  const extractStopsellDates = useExtractStopsellDates();

  const [extraction, setExtraction] = useState<StopsellExtraction | null>(null);
  const [roomTypeOverrides, setRoomTypeOverrides] = useState<Record<number, string>>({});

  const catalogRooms = useMemo<Array<CatalogRoom>>(() => {
    const seen = new Map<string, CatalogRoom>();
    roomRateSelections.forEach(({ roomType }) => {
      if (!seen.has(roomType.id)) {
        seen.set(roomType.id, { id: roomType.id, name: roomType.name, roomRates: [] });
      }
    });
    return Array.from(seen.values());
  }, [roomRateSelections]);

  useEffect(() => {
    if (!extraction || extraction.rooms.length === 0) {
      return;
    }

    const matches = matchRoomsToCatalog(
      extraction.rooms.map((room) => ({ name: room.name, roomTypeId: room.roomTypeId })),
      catalogRooms,
    );
    setRoomTypeOverrides(Object.fromEntries(matches.map((match, index) => [index, match.catalogRoomId ?? UNMAPPED])));
    // Only re-derive the initial guess when a new extraction lands, not on every catalogRooms re-render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extraction]);

  const docRangesByRoomType = useMemo(() => {
    const map: Record<string, Array<SourceBlackoutRange>> = {};
    extraction?.rooms.forEach((room, roomIndex) => {
      const roomTypeId = roomTypeOverrides[roomIndex];
      if (!roomTypeId) {
        return;
      }
      const ranges: Array<SourceBlackoutRange> = room.dates.map((date, dateIndex) => ({
        id: `doc-${roomIndex}-${dateIndex}`,
        start: date.start,
        end: date.end,
      }));
      map[roomTypeId] = [...(map[roomTypeId] ?? []), ...ranges];
    });
    return map;
  }, [extraction, roomTypeOverrides]);

  const { changedPlans, excludedRoomTypeIds, toggleRoomType, totalToCreate, totalToDelete, handleApply, isApplying } =
    useBlackoutApplyPlan({
      blackoutDates: NO_SALESFORCE_BLACKOUT_DATES,
      docRangesByRoomType,
      blackoutDatesByRoomRate,
      propertyId,
      roomRateSelections,
      roomTypeNames: [],
      onApplied: onClose,
    });

  const showUpload = extraction === null;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" gap={1}>
          <EventBusyOutlinedIcon color="warning" />
          <Typography variant="h6">Upload stopsell document</Typography>
        </Stack>
        {extraction && extraction.rooms.length > 0 && (
          <Chip label={`${extraction.rooms.length} room(s) from document`} size="small" sx={{ mt: 1 }} />
        )}
      </DialogTitle>

      <DialogContent dividers>
        {showUpload && (
          <Stack gap={2}>
            <Typography color="text.secondary" variant="body2">
              Upload a stopsell document to apply its blackout dates directly to matched room types.
            </Typography>
            <Typography color="warning.dark" variant="body2">
              Exercise caution, as this can override all current blackouts configured; for any conflicts with known
              contractual Salesforce blackouts, please advise Campaign Manager and get approval.
            </Typography>
            <AvailabilityDocumentUploadZone
              isPending={extractStopsellDates.isPending}
              error={extractStopsellDates.error ? 'Could not read that document. Please try again.' : undefined}
              onSelectFile={(file) => extractStopsellDates.mutate({ file, catalogRooms }, { onSuccess: setExtraction })}
            />
          </Stack>
        )}

        {!showUpload && (
          <Stack gap={3}>
            {extraction && extraction.rooms.length > 0 && (
              <Stack gap={1.5}>
                <Typography variant="subtitle2">Map document rooms to room types</Typography>
                {extraction.rooms.map((room, roomIndex) => (
                  <Stack key={`${room.name}-${roomIndex}`} direction="row" alignItems="center" gap={2}>
                    <Typography variant="body2" sx={{ minWidth: 200 }}>
                      {room.name}
                    </Typography>
                    <FormControl size="small" sx={{ minWidth: 220 }}>
                      <Select
                        value={roomTypeOverrides[roomIndex] ?? UNMAPPED}
                        onChange={(event) =>
                          setRoomTypeOverrides((prev) => ({ ...prev, [roomIndex]: event.target.value }))
                        }
                        displayEmpty
                      >
                        <MenuItem value={UNMAPPED}>
                          <Typography component="span" fontStyle="italic">
                            Unmapped (ignore this room)
                          </Typography>
                        </MenuItem>
                        {catalogRooms.map((catalogRoom) => (
                          <MenuItem key={catalogRoom.id} value={catalogRoom.id}>
                            {catalogRoom.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                ))}
                <Divider />
              </Stack>
            )}

            <BlackoutApplyReviewTable
              changedPlans={changedPlans}
              excludedRoomTypeIds={excludedRoomTypeIds}
              showScopeColumn={false}
              toggleRoomType={toggleRoomType}
            />
          </Stack>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
        <Stack direction="row" gap={2} alignItems="center">
          {(totalToCreate > 0 || totalToDelete > 0) && (
            <Typography variant="body2" color="text.secondary">
              <strong>{pluralizeToString('range', totalToCreate)}</strong> to create,{' '}
              <strong>{pluralizeToString('block', totalToDelete)}</strong> to remove
            </Typography>
          )}
        </Stack>
        <Stack direction="row" gap={1}>
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          {!showUpload && (
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={isApplying || totalToCreate + totalToDelete === 0}
            >
              Apply
            </Button>
          )}
        </Stack>
      </DialogActions>
    </Dialog>
  );
}
